import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, rtdb } from '../firebase/config';
import { ref, onDisconnect, remove } from 'firebase/database';
import { usePresenceSync } from '../hooks/usePresenceSync';

const AuthContext = createContext();

/*
 * PresenceSyncMounter — monté UNIQUEMENT pour les marchands.
 * Reçoit le shopId résolu pour l'écoute RTDB ciblée.
 * Plus de getDocs global : 0 lecture Firestore au login.
 */
const PresenceSyncMounter = ({ shopId }) => {
  usePresenceSync(shopId);
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [userRole, setUserRole]   = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  /* shopId du marchand connecté — nécessaire pour usePresenceSync ciblé */
  const [merchantShopId, setMerchantShopId] = useState(null);

  useEffect(() => {
    let unsubUser = () => {};

    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      unsubUser();
      /* Réinitialiser le shopId à la déconnexion */
      setMerchantShopId(null);

      if (firebaseUser) {
        unsubUser = onSnapshot(doc(db, 'users', firebaseUser.uid), async (snap) => {
          const userData = snap.data();
          if (!userData) return;

          const suspended = userData.suspended === true;
          setIsSuspended(suspended);

          if (suspended) {
            signOut(auth);
            return;
          }

          const role = userData?.role || 'user';
          setUser({ ...firebaseUser, ...userData });
          setUserRole(role);
          setLoading(false);

          /*
           * Pour les marchands : résoudre le shopId une seule fois.
           * 1 lecture Firestore au login du marchand (au lieu de N lectures
           * pour tous les shops pour tous les users comme avant).
           */
          if (role === 'merchant' && !merchantShopId) {
            try {
              const mDoc = await getDoc(doc(db, 'merchants', firebaseUser.uid));
              const sId = mDoc.data()?.shopId || null;
              setMerchantShopId(sId);
            } catch (err) {
              console.warn('AuthContext: impossible de récupérer shopId', err);
            }
          }
        }, (err) => {
          console.error('Error listening user data:', err);
          setUser(firebaseUser);
          setUserRole('user');
          setLoading(false);
        });
      } else {
        setUser(null);
        setUserRole(null);
        setIsSuspended(false);
        setLoading(false);
      }
    });

    return () => { unsubAuth(); unsubUser(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = async (email, password, name, role = 'user') => {
    const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);

    await setDoc(doc(db, 'users', firebaseUser.uid), {
      name,
      email,
      role,
      createdAt: new Date().toISOString()
    });

    if (role === 'merchant') {
      await setDoc(doc(db, 'merchants', firebaseUser.uid), {
        userId:  firebaseUser.uid,
        hasShop: false,
      });
    }

    return firebaseUser;
  };

  const login = async (email, password) => {
    const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);
    return firebaseUser;
  };

  const logout = async () => {
    /* Désarmer le nœud presence avant déconnexion */
    if (userRole === 'merchant' && rtdb && merchantShopId) {
      try {
        const presenceRef = ref(rtdb, `presence/${merchantShopId}`);
        await onDisconnect(presenceRef).cancel();
        await remove(presenceRef);
      } catch (err) {
        console.warn('AuthContext: erreur désarmement onDisconnect au logout', err);
      }
    }
    await signOut(auth);
  };

  /* ── Suppression complète du compte commerçant ─────────────────
     Ordre :
     1. Réauthentifie (Firebase exige une session fraîche pour deleteUser)
     2. Supprime /shops/{shopId}
     3. Supprime présence RTDB
     4. Supprime /merchants/{uid}
     5. Supprime /users/{uid}
     6. Supprime le compte Firebase Auth
  ─────────────────────────────────────────────────────────────── */
  const deleteAccount = async (password) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Utilisateur non connecté.');

    const credential = EmailAuthProvider.credential(currentUser.email, password);
    await reauthenticateWithCredential(currentUser, credential);

    /* Utiliser le shopId déjà résolu si disponible */
    let shopId = merchantShopId;
    if (!shopId) {
      try {
        const mDoc = await getDoc(doc(db, 'merchants', currentUser.uid));
        shopId = mDoc.data()?.shopId || null;
      } catch { /* non bloquant */ }
    }

    if (shopId) {
      try { await deleteDoc(doc(db, 'shops', shopId)); } catch { /* non bloquant */ }
    }

    if (shopId && rtdb) {
      try {
        const presenceRef = ref(rtdb, `presence/${shopId}`);
        await onDisconnect(presenceRef).cancel();
        await remove(presenceRef);
      } catch { /* non bloquant */ }
    }

    try { await deleteDoc(doc(db, 'merchants', currentUser.uid)); } catch { /* non bloquant */ }
    try { await deleteDoc(doc(db, 'users', currentUser.uid)); } catch { /* non bloquant */ }
    await deleteUser(currentUser);
  };

  return (
    <AuthContext.Provider value={{
      user,
      userRole,
      loading,
      register,
      login,
      logout,
      isMerchant:   userRole === 'merchant',
      isAdmin:      userRole === 'admin',
      isSuspended,
      deleteAccount,
      merchantShopId,
    }}>
      {/*
        PresenceSyncMounter monté UNIQUEMENT pour les marchands.
        Passe le shopId résolu → 1 listener RTDB ciblé, 0 lecture globale.
      */}
      {user && userRole === 'merchant' && merchantShopId && (
        <PresenceSyncMounter shopId={merchantShopId} />
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);